import { useCallback, useEffect, useState } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';

/** Resposta da API em { ok, error }; o erro vem em `error` (HTTP 400) ou `message`. */
const outcome = (result) => {
  if (result && result.status === 'SUCCESS') return { ok: true, id: result.data?.id };
  return { ok: false, error: result?.error || result?.message || 'Não foi possível concluir. Tente de novo.' };
};

const dateOnly = (value) => (value ? String(value).slice(0, 10) : '');

/** Todos os campos atuais do pedido, como a API de update espera. */
const requestPayload = (patientId, request) => ({
  id: request.id,
  patientId,
  title: request.title,
  requesting_professional: request.requesting_professional || '',
  issued_at: dateOnly(request.issued_at),
  valid_until: dateOnly(request.valid_until),
  notes: request.notes || '',
});

/** Todos os campos atuais da guia: update_* sobrescreve tudo, então nada pode faltar. */
export const authorizationPayload = (patientId, authorization) => ({
  id: authorization.id,
  patientId,
  provider_id: authorization.provider_id,
  patient_insurance_id: authorization.patient_insurance_id || '',
  medical_request_id: authorization.medical_request_id || '',
  authorization_type: authorization.authorization_type || 'consultation',
  reference_number: authorization.reference_number || '',
  password: authorization.password || '',
  status: authorization.status || 'pending',
  valid_from: dateOnly(authorization.valid_from),
  valid_until: dateOnly(authorization.valid_until),
  authorized_quantity: authorization.authorized_quantity || '',
  notes: authorization.notes || '',
});

const isActive = (item) => item.active !== 0 && item.active !== false;

/** Ativos primeiro, inativos no fim, sem mudar a ordem que a API devolveu. */
const activeFirst = (list) => [...(list || [])].sort((a, b) => Number(isActive(b)) - Number(isActive(a)));

/**
 * Pedidos médicos e guias da pessoa: carga, gravação, troca de situação e
 * inativar/reativar. Cada gravação devolve { ok, error } e, quando dá certo,
 * recarrega a lista (os contadores e o "vencido" vêm calculados do backend).
 * saveRequest/saveAuthorization não recarregam: o formulário ainda envia os
 * anexos, e quem chama recarrega uma vez no onSaved (com a contagem de anexos).
 */
export default function useInsuranceDocuments(patientId) {
  const {
    GetPatientInsuranceDocuments, SetMedicalRequest, UpdateMedicalRequest,
    SetInsuranceAuthorization, UpdateInsuranceAuthorization,
  } = DataProviderInstance();

  const [requests, setRequests] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setToast({ open: true, message, severity });
  const closeToast = () => setToast((prev) => ({ ...prev, open: false }));

  const reload = useCallback(() => {
    if (patientId === undefined || patientId === null) return;
    GetPatientInsuranceDocuments(patientId, (result) => {
      if (result && result.status === 'SUCCESS') {
        setRequests(activeFirst(result.data?.requests));
        setAuthorizations(activeFirst(result.data?.authorizations));
        setLoadError('');
      } else {
        setLoadError(result?.error || result?.message || 'Não foi possível carregar os documentos.');
      }
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload]);

  /** Executa a chamada, recarrega quando deu certo (salvo `skipReload`) e devolve { ok, error }. */
  const run = (call, successMessage, skipReload = false) => new Promise((resolve) => {
    setBusy(true);
    call((result) => {
      const done = outcome(result);
      setBusy(false);
      if (done.ok) {
        if (!skipReload) reload();
        if (successMessage) notify(successMessage);
      }
      resolve(done);
    });
  });

  // Sem recarga aqui: o formulário envia os anexos e o onSaved recarrega uma vez
  const saveRequest = (values, existing) => (existing
    ? run((cb) => UpdateMedicalRequest({ ...requestPayload(patientId, existing), ...values }, cb), 'Pedido atualizado', true)
    : run((cb) => SetMedicalRequest({ patientId, ...values }, cb), 'Pedido registrado', true));

  const setRequestActive = (request, active) => run(
    (cb) => UpdateMedicalRequest({ ...requestPayload(patientId, request), active }, cb),
    active ? 'Pedido reativado' : 'Pedido inativado',
  ).then((done) => { if (!done.ok) notify(done.error, 'error'); return done; });

  const saveAuthorization = (values, existing) => (existing
    ? run((cb) => UpdateInsuranceAuthorization({ ...authorizationPayload(patientId, existing), ...values }, cb), 'Guia atualizada', true)
    : run((cb) => SetInsuranceAuthorization({ patientId, ...values }, cb), 'Guia registrada', true));

  const setAuthorizationActive = (authorization, active) => run(
    (cb) => UpdateInsuranceAuthorization({ ...authorizationPayload(patientId, authorization), active }, cb),
    active ? 'Guia reativada' : 'Guia inativada',
  ).then((done) => { if (!done.ok) notify(done.error, 'error'); return done; });

  const changeAuthorizationStatus = (authorization, status) => run(
    (cb) => UpdateInsuranceAuthorization({ ...authorizationPayload(patientId, authorization), status }, cb),
    'Situação da guia atualizada',
  ).then((done) => { if (!done.ok) notify(done.error, 'error'); return done; });

  return {
    requests,
    authorizations,
    loading,
    busy,
    loadError,
    reload,
    saveRequest,
    setRequestActive,
    saveAuthorization,
    setAuthorizationActive,
    changeAuthorizationStatus,
    toast,
    closeToast,
  };
}
